import React from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatLocalizedQuantity } from '../../../../utils/numberFormatting';
import { formatUnit } from '../../../../utils/units';
import { getOrderProductDisplayDimensions, getOrderProductDisplayName } from '../../orderProductDisplay';
import { Callout, InfoList, StepSection } from '../OrderProcessParts';
import {
  getOrderDraftLineLogistics,
  getOrderDraftLogisticsTotals,
  type OrderDraftLine,
  type OrderDraftLogistics,
} from './orderDraftModel';

/**
 * „Ściąga logistyczna” (makieta `sSzkic`, right card) without pallets — the
 * pallet model was removed on 2026-09-30. It shows what the product has
 * today: format, thickness and piece weight, and the amounts of the line and
 * of the whole order (szt., m², m³, weight) when they can be derived.
 */
export default function OrderDraftLogisticsCard({ line, lines }: { line: OrderDraftLine | null; lines: readonly OrderDraftLine[] }) {
  const { t, i18n } = useTranslation(['orders']);
  const language = i18n.language;
  const quantity = (value: number | null, unit: string) => (value == null
    ? '—'
    : `${formatLocalizedQuantity(value, language)} ${formatUnit(unit, language)}`);
  const weight = (value: number | null) => (value == null ? '—' : `≈ ${formatLocalizedQuantity(Math.round(value), language)} kg`);
  const amounts = (logistics: OrderDraftLogistics): Array<[React.ReactNode, React.ReactNode]> => [
    [t('orders:process.draft.cheat.pieces'), quantity(logistics.pieces, 'pcs')],
    [t('orders:process.draft.cheat.area'), quantity(logistics.m2, 'm2')],
    [t('orders:process.draft.cheat.volume'), quantity(logistics.m3, 'm3')],
    [t('orders:process.draft.cheat.weight'), weight(logistics.weightKg)],
  ];

  if (!line) {
    return (
      <StepSection title={t('orders:process.draft.cheat.title')} icon={<InboxOutlined aria-hidden="true" />} testId="zk-draft-cheat">
        <p className="zk-section__hint">{t('orders:process.draft.cheat.empty')}</p>
      </StepSection>
    );
  }
  const dimensions = getOrderProductDisplayDimensions(line as never);
  const thickness = Number(line.thickness);
  const pieceWeight = Number(line.grossWeight);
  return (
    <StepSection
      title={t('orders:process.draft.cheat.title')}
      icon={<InboxOutlined aria-hidden="true" />}
      hint={t('orders:process.draft.cheat.hint')}
      testId="zk-draft-cheat"
    >
      <div className="zk-draft__cheat-body">
        <div>
          <div className="zk-draft__cheat-product">
            <small>{t('orders:process.lines.product')}</small>
            <strong data-testid="zk-draft-cheat-product">{getOrderProductDisplayName(line as never, line.name || '')}</strong>
            {dimensions && <small className="zk-draft__cheat-dimensions" data-testid="zk-draft-cheat-dimensions">{dimensions}</small>}
          </div>
          <InfoList rows={[
            [t('orders:process.draft.cheat.format'), dimensions || '—'],
            [t('orders:process.draft.cheat.thickness'), thickness > 0 ? `${formatLocalizedQuantity(thickness, language)} mm` : '—'],
            [t('orders:process.draft.cheat.pieceWeight'), pieceWeight > 0 ? `${formatLocalizedQuantity(pieceWeight, language)} kg` : '—'],
            ...amounts(getOrderDraftLineLogistics(line)),
          ]} />
        </div>
        <div>
          <h4 className="zk-section__group-title zk-draft__cheat-total-title">{t('orders:process.draft.cheat.wholeOrder')}</h4>
          <InfoList rows={amounts(getOrderDraftLogisticsTotals(lines))} />
        </div>
      </div>
      <Callout tone="muted">{t('orders:process.draft.cheat.source')}</Callout>
    </StepSection>
  );
}
